import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthStateService } from './auth-state.service';

export interface LoginResponse {
  token: string;
  username: string;
  role: string;
  message?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly LAST_URL_KEY = 'last_url';
  private get API_URL(): string {
    return `${environment.apiUrl}/auth`;
  }

  constructor(
    private http: HttpClient,
    private authStateService: AuthStateService
  ) {
    // Expose debug methods to window for console access
    (window as any).authDebug = {
      reloadUserData: () => this.debugReloadUserData(),
      checkStorage: () => this.debugCheckStorage(),
      getUser: () => this.getUser(),
      isAuthenticated: () => this.isAuthenticated(),
      initializeAuth: () => this.initializeAuthentication(),
      testRestoration: () => this.testAuthenticationRestoration(),
      clearAllData: () => this.clearAllAuthData(),
      forceLogout: () => this.forceLogout(),
      checkAuthState: () => this.authStateService.checkAuthState(),
      resetAuth: () => this.resetAuthentication()
    };
    
    // Initialize authentication state on service creation
    this.initializeAuthentication();
    

  }

  // Initialize authentication state and ensure user data is properly loaded
  initializeAuthentication(): void {
    const token = this.getToken();
    const user = this.getUser();
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    
    console.log('🔐 [AUTH] Initializing authentication...');
    console.log('🔐 [AUTH] Token exists:', !!token);
    console.log('🔐 [AUTH] User exists:', !!user);
    console.log('🔐 [AUTH] Remember me:', rememberMe);
    
    if (token && !user) {
      console.log('🔐 [AUTH] Token found but no user data, restoring from backend...');
      this.restoreUserDataFromToken();
    } else if (token && user) {
      console.log('🔐 [AUTH] Token and user data found, setting authenticated');
      this.authStateService.setAuthenticated(true);
    } else {
      console.log('🔐 [AUTH] No valid authentication found, setting unauthenticated');
      this.authStateService.setAuthenticated(false);
    }
  }

  // Restore user data from backend using existing token
  private restoreUserDataFromToken(): void {
    console.log('🔐 [AUTH] Attempting to restore user data from token...');
    
    // Make a request to get user info using the existing token
    this.http.get<any>(`${this.API_URL}/me`).pipe(
      tap(response => {
        console.log('🔐 [AUTH] Successfully restored user data from backend');
        
        // Store the user data
        const userObject = {
          username: response.username,
          role: response.role,
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email
        };
        
        const rememberMe = localStorage.getItem('remember_me') === 'true';
        this.setUser(userObject, rememberMe);
        
        // Set authentication state
        this.authStateService.setAuthenticated(true);
      }),
      catchError(error => {
        console.error('❌ [AUTH] Failed to restore user data from backend:', error);
        console.log('❌ [AUTH] Clearing authentication data due to failed restoration');
        this.clearAuthData();
        return throwError(() => error);
      })
    ).subscribe();
  }

  login(usernameOrEmail: string, password: string, rememberMe: boolean = false): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/login`, { username: usernameOrEmail, password, rememberMe }).pipe(
      tap(response => {
        this.setToken(response.token, rememberMe);
        
        const userObject = { 
          username: response.username, 
          role: response.role, 
          firstName: response.firstName, 
          lastName: response.lastName, 
          email: response.email 
        };
        
        this.setUser(userObject, rememberMe);
        this.authStateService.setAuthenticated(true);
      }),
      catchError(error => {
        console.error('❌ [AUTH] Login failed:', error);
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${this.API_URL}/logout`, {}).pipe(
      tap(() => {
        this.clearAuthData();
      }),
      catchError(error => {
        // Even if backend call fails, clear local storage
        this.clearAuthData();
        return throwError(() => error);
      })
    );
  }

  private clearAuthData(): void {
    // Clear all authentication data
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expiration');
    localStorage.removeItem(this.LAST_URL_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem('token_expiration');
    sessionStorage.removeItem(this.LAST_URL_KEY);
    
    // Update authentication state
    this.authStateService.setAuthenticated(false);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    
    console.log('🔐 [AUTH] Checking authentication...');
    console.log('🔐 [AUTH] Token exists:', !!token);
    console.log('🔐 [AUTH] Remember me:', rememberMe);
    
    if (!token) {
      console.log('🔐 [AUTH] No token found, not authenticated');
      return false;
    }
    
    // Check token expiration
    if (rememberMe) {
      const expirationTime = localStorage.getItem('token_expiration');
      if (expirationTime && new Date().getTime() > parseInt(expirationTime)) {
        console.log('🔐 [AUTH] Token expired (remember me), clearing data');
        this.clearAuthData();
        return false;
      }
      const hasToken = !!localStorage.getItem(this.TOKEN_KEY);
      console.log('🔐 [AUTH] Remember me session, has token:', hasToken);
      return hasToken;
    } else {
      const expirationTime = sessionStorage.getItem('token_expiration');
      if (expirationTime && new Date().getTime() > parseInt(expirationTime)) {
        console.log('🔐 [AUTH] Token expired (session), clearing data');
        this.clearAuthData();
        return false;
      }
      // Check both sessionStorage and localStorage for token
      const sessionToken = !!sessionStorage.getItem(this.TOKEN_KEY);
      const localToken = !!localStorage.getItem(this.TOKEN_KEY);
      const hasToken = sessionToken || localToken;
      console.log('🔐 [AUTH] Session storage token:', sessionToken);
      console.log('🔐 [AUTH] Local storage token:', localToken);
      console.log('🔐 [AUTH] Has token:', hasToken);
      return hasToken;
    }
  }

  getToken(): string | null {
    const localToken = localStorage.getItem(this.TOKEN_KEY);
    const sessionToken = sessionStorage.getItem(this.TOKEN_KEY);
    const token = localToken || sessionToken;
    
    console.log('🔐 [AUTH] getToken() - localStorage:', !!localToken, 'sessionStorage:', !!sessionToken);
    
    return token;
  }

  private setToken(token: string, rememberMe: boolean = false): void {
    // Calculate expiration time (24 hours for session, 7 days for remember me)
    const expirationTime = rememberMe ? 
      new Date().getTime() + (7 * 24 * 60 * 60 * 1000) : // 7 days
      new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
    
    if (rememberMe) {
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem('token_expiration', expirationTime.toString());
      localStorage.setItem('remember_me', 'true');
    } else {
      sessionStorage.setItem(this.TOKEN_KEY, token);
      sessionStorage.setItem('token_expiration', expirationTime.toString());
      localStorage.setItem('remember_me', 'false');
    }
    
    console.log('🔐 [AUTH] Token stored with expiration:', new Date(expirationTime));
  }

  private setUser(user: any, rememberMe: boolean = false): void {
    const userStr = JSON.stringify(user);
    
    try {
      if (rememberMe) {
        localStorage.setItem(this.USER_KEY, userStr);
      } else {
        sessionStorage.setItem(this.USER_KEY, userStr);
      }
    } catch (error) {
      console.error('❌ [AUTH] Error storing user data:', error);
    }
  }

  getUser(): any {
    const localStorageUser = localStorage.getItem(this.USER_KEY);
    const sessionStorageUser = sessionStorage.getItem(this.USER_KEY);
    const userStr = localStorageUser || sessionStorageUser;
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user;
      } catch (error) {
        console.error('❌ [AUTH] Error parsing user data:', error);
        return null;
      }
    } else {
      return null;
    }
  }

  // Test method to manually trigger authentication restoration
  testAuthenticationRestoration(): void {
    const token = this.getToken();
    const user = this.getUser();
    
    if (token && !user) {
      this.restoreUserDataFromToken();
    }
  }

  // Debug method to manually clear and reload user data
  debugReloadUserData(): void {
    localStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }

  // Debug method to check current storage state
  debugCheckStorage(): void {
    console.log('🔍 [AUTH-DEBUG] === Authentication Debug Information ===');
    
    // Check tokens
    const localStorageToken = localStorage.getItem(this.TOKEN_KEY);
    const sessionStorageToken = sessionStorage.getItem(this.TOKEN_KEY);
    console.log('🔍 [AUTH-DEBUG] localStorage token:', !!localStorageToken);
    console.log('🔍 [AUTH-DEBUG] sessionStorage token:', !!sessionStorageToken);
    
    // Check user data
    const localStorageUser = localStorage.getItem(this.USER_KEY);
    const sessionStorageUser = sessionStorage.getItem(this.USER_KEY);
    console.log('🔍 [AUTH-DEBUG] localStorage user:', !!localStorageUser);
    console.log('🔍 [AUTH-DEBUG] sessionStorage user:', !!sessionStorageUser);
    
    // Check remember me
    const rememberMe = localStorage.getItem('remember_me');
    console.log('🔍 [AUTH-DEBUG] Remember me:', rememberMe);
    
    // Check expiration times
    const localStorageExpiration = localStorage.getItem('token_expiration');
    const sessionStorageExpiration = sessionStorage.getItem('token_expiration');
    console.log('🔍 [AUTH-DEBUG] localStorage expiration:', localStorageExpiration);
    console.log('🔍 [AUTH-DEBUG] sessionStorage expiration:', sessionStorageExpiration);
    
    // Check current time vs expiration
    const currentTime = new Date().getTime();
    if (localStorageExpiration) {
      const isExpired = currentTime > parseInt(localStorageExpiration);
      console.log('🔍 [AUTH-DEBUG] localStorage token expired:', isExpired);
    }
    if (sessionStorageExpiration) {
      const isExpired = currentTime > parseInt(sessionStorageExpiration);
      console.log('🔍 [AUTH-DEBUG] sessionStorage token expired:', isExpired);
    }
    
    // Check authentication state
    const isAuth = this.isAuthenticated();
    console.log('🔍 [AUTH-DEBUG] isAuthenticated():', isAuth);
    
    console.log('🔍 [AUTH-DEBUG] === End Debug Information ===');
  }



  setLastUrl(url: string): void {
    sessionStorage.setItem(this.LAST_URL_KEY, url);
  }

  getLastUrl(): string {
    return sessionStorage.getItem(this.LAST_URL_KEY) || '/home';
  }

  // Start periodic token expiration check
  startTokenExpirationCheck(): void {
    // Check token expiration every 5 minutes
    setInterval(() => {
      if (!this.isAuthenticated()) {
        this.clearAuthData();
        // You might want to emit an event here to notify the app
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Check if current session is a remember me session
  isRememberMeSession(): boolean {
    return localStorage.getItem('remember_me') === 'true';
  }

  // Force logout - clears all data without backend call
  forceLogout(): void {
    console.log('🔐 [AUTH] Force logout - clearing all authentication data');
    this.clearAuthData();
  }

  // Clear all authentication data and reset state
  clearAllAuthData(): void {
    console.log('🔐 [AUTH] Clearing all authentication data...');
    
    // Clear all possible authentication-related data
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expiration');
    localStorage.removeItem(this.LAST_URL_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem('token_expiration');
    sessionStorage.removeItem(this.LAST_URL_KEY);
    
    // Also clear any other potential auth-related items
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    
    // Update authentication state
    this.authStateService.setAuthenticated(false);
    
    console.log('🔐 [AUTH] All authentication data cleared');
  }

  // Reset authentication state completely
  resetAuthentication(): void {
    console.log('🔐 [AUTH] Resetting authentication state...');
    this.clearAllAuthData();
    
    // Force a fresh check
    setTimeout(() => {
      this.authStateService.checkAuthState();
    }, 100);
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.get(`${this.API_URL}/verify-email?token=${token}`).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Email verification failed' });
      })
    );
  }

  signup(signupData: any): Observable<any> {
    return this.http.post(`${this.API_URL}/signup`, signupData).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Registration failed' });
      })
    );
  }

  getPendingApprovals(): Observable<any> {
    return this.http.get(`${this.API_URL}/pending-approvals`).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Failed to load pending approvals' });
      })
    );
  }

  approveUser(userId: number, role: string): Observable<any> {
    return this.http.post(`${this.API_URL}/approve/${userId}?role=${role}`, {}, { responseType: 'text' }).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Failed to approve user' });
      })
    );
  }

  rejectUser(userId: number): Observable<any> {
    return this.http.post(`${this.API_URL}/reject/${userId}`, {}, { responseType: 'text' }).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Failed to reject user' });
      })
    );
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.API_URL}/forgot-password`, { email }).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Failed to send password reset email' });
      })
    );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.API_URL}/reset-password`, { token, newPassword }).pipe(
      catchError(error => {
        return throwError(() => error.error || { message: 'Failed to reset password' });
      })
    );
  }

  // Cleanup method to remove debug object
  ngOnDestroy(): void {
    if ((window as any).authDebug) {
      delete (window as any).authDebug;
    }
  }
} 